import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { deleteWork, fetchWork, hasChildren, moveWork } from './api';
import { WorkModal } from './WorkModal';
import { WorkTree } from './WorkTree';
import { canCreateChild, type TreeNode } from './treeRules';

/** `null` — closed; `create` — new work under `parentId` (0 = section); `edit` — the selected work. */
type ModalState = { mode: 'create'; parentId: number } | { mode: 'edit' } | null;

const show = (value: unknown) => (value ? String(value) : '-');

const deleteQuestion = ({ work }: TreeNode) =>
  `Вы действительно хотите удалить ${
    !work.parent_id || hasChildren(work) ? 'данную услугу и все связанные с ней пункты' : 'этот пункт'
  }?`;

export const WorksPage = () => {
  const qc = useQueryClient();
  const [selected, setSelected] = useState<TreeNode | null>(null);
  const [modal, setModal] = useState<ModalState>(null);
  const [dragging, setDragging] = useState<TreeNode | null>(null);
  const [actionError, setActionError] = useState('');

  const current = useQuery({
    queryKey: ['works', 'view', selected?.work.id],
    queryFn: () => fetchWork(selected!.work.id),
    enabled: selected !== null,
  });
  const work = selected === null ? undefined : current.data;

  const mutationOptions = {
    onSuccess: () => qc.invalidateQueries({ queryKey: ['works'] }),
    onError: (error: Error) => setActionError(error.message),
  };
  const move = useMutation({
    mutationFn: ({ id, parentId }: { id: number; parentId: number }) => moveWork(id, parentId),
    ...mutationOptions,
  });
  const remove = useMutation({ mutationFn: deleteWork, ...mutationOptions });

  const onDropOn = (source: TreeNode, target: TreeNode | null) => {
    setDragging(null);
    const question = target
      ? `Вы пытаетесь изменить вложенность. Сделать "${source.work.name}" ребенком для "${target.work.name}".`
      : `Вы пытаетесь изменить вложенность. Сделать "${source.work.name}" корневым элементом.`;
    if (!window.confirm(question)) return;
    setActionError('');
    move.mutate({ id: source.work.id, parentId: target?.work.id ?? 0 });
  };

  const onDelete = (node: TreeNode) => {
    if (!window.confirm(deleteQuestion(node))) return;
    setActionError('');
    remove.mutate(node.work.id, {
      onSuccess: () => setSelected((prev) => (prev?.work.id === node.work.id ? null : prev)),
    });
  };

  return (
    <>
      <section className="content-header">
        <h1>Услуги</h1>
        <div className="row">
          <div className="col-sm-12">
            <button
              type="button"
              className="btn btn-info pull-right"
              onClick={() => setModal({ mode: 'create', parentId: 0 })}
            >
              Создать
            </button>
          </div>
        </div>
      </section>
      <section className="content">
        <div className="row">
          <div className="col-md-4">
            <div className="box">
              <div className="box-body">
                <WorkTree
                  selectedId={selected?.work.id ?? null}
                  onSelect={(node) => {
                    setSelected(node);
                    setActionError('');
                  }}
                  onDelete={onDelete}
                  dragging={dragging}
                  onDragChange={setDragging}
                  onDropOn={onDropOn}
                />
              </div>
            </div>
          </div>
          <div className="col-md-8">
            <div className="box">
              <div className="box-header with-border">
                <h3 className="box-title">{work?.name || '-'}</h3>
              </div>
              <div className="box-body">
                <div><b>Id:</b> {show(work?.id)}</div>
                <div><b>Alias:</b> {show(work?.alias)}</div>
                <div><b>Title:</b> {show(work?.title)}</div>
                <div><b>Icon:</b> {show(work?.icon)}</div>
                <div><b>Parent id:</b> {show(work?.parent_id)}</div>
                <div><b>Is active:</b> {show(work?.is_active)}</div>
                {selected && work && (
                  <div style={{ marginTop: 12 }}>
                    {canCreateChild(selected) && (
                      <button
                        type="button"
                        className="btn btn-info"
                        style={{ marginRight: 8 }}
                        onClick={() => setModal({ mode: 'create', parentId: work.id })}
                      >
                        Create Child
                      </button>
                    )}
                    <button type="button" className="btn btn-success" onClick={() => setModal({ mode: 'edit' })}>
                      Edit
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
        {actionError && <div className="alert alert-danger">{actionError}</div>}
      </section>
      {modal?.mode === 'create' && (
        <WorkModal parentId={modal.parentId} onClose={() => setModal(null)} />
      )}
      {modal?.mode === 'edit' && work && <WorkModal work={work} onClose={() => setModal(null)} />}
    </>
  );
};
